import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowRight } from "lucide-react";
import { useAuth, destinationFor } from "@/context/AuthContext";
import { apiError } from "@/lib/journeyApi";
import { JourneyTopBar, JourneyFooter } from "@/components/JourneyChrome";
import { PasswordField } from "@/components/PasswordField";

const field = "w-full rounded-xl pe-surface py-3 pl-10 pr-3 text-[15px] text-[#171717] outline-none transition-colors focus:border-[#2F6B57] focus:ring-2 focus:ring-[#2F6B57]/25";

export default function Login() {
  const navigate = useNavigate();
  const { loginEmail, googleRedirect } = useAuth();
  const [method, setMethod] = useState("email"); // email | google
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const route = (me) => {
    const dest = destinationFor(me);
    if (dest) {
      toast.success("Welcome back.");
      // Honour a deep-link destination (?next=) when it is a safe in-app path
      // AND the customer is entitled (dest is the gated area, not the sales page).
      const params = new URLSearchParams(window.location.search);
      const next = params.get("next");
      const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : null;
      // Only follow a customer deep-link (?next) for an ENTITLED customer. An
      // Owner/Admin without entitlement must go to their admin hub, never into
      // the customer entitlement gate (which would bounce back to /login -> loop).
      const useNext = me?.entitled && safeNext && dest.startsWith("/dashboard");
      navigate(useNext ? safeNext : dest, { replace: true });
    } else {
      toast.error("No active The Paycheck Edit found for this account. Purchase to get access.");
      navigate("/", { replace: true });
    }
  };

  const signIn = async () => {
    if (!email.trim()) return toast.error("Enter your email.");
    if (!password) return toast.error("Enter your password.");
    setBusy(true);
    try {
      route(await loginEmail({ email: email.trim().toLowerCase(), password }));
    } catch (e) {
      toast.error(apiError(e, "Could not sign in."));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#FAF8F4] text-[#171717]" data-testid="login-page">
      <JourneyTopBar showSignIn={false} />
      <main className="flex flex-1 items-center justify-center px-5 py-12">
        <div className="w-full max-w-md">
          <div className="text-center">
            <h1 className="font-serif text-3xl text-[#171717]">Welcome back</h1>
            <p className="mt-2 text-[15px] text-[#525252]">Sign in to your Paycheck Edit.</p>
          </div>

          <div className="mt-6 rounded-2xl pe-surface p-6">
            <div className="grid grid-cols-2 gap-2" data-testid="login-method-toggle">
              {[["email", "Email + password"], ["google", "Continue with Google"]].map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setMethod(v)}
                  data-testid={`login-method-${v}`}
                  className={`rounded-lg border px-3 py-2 text-[13px] font-medium transition-colors ${method === v ? "border-[#2F6B57] bg-[#DDE7DF] text-[#164C3B]" : "border-[#E5E0D9] bg-white text-[#70584A] hover:bg-[#F5F2EC]"}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {method === "email" ? (
              <div className="mt-5 space-y-3">
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A89A88]" />
                  <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className={field} />
                </div>
                <PasswordField testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" autoComplete="current-password" onKeyDown={(e) => e.key === "Enter" && signIn()} />
                <div className="text-right">
                  <Link to="/forgot" data-testid="forgot-link" className="text-xs font-medium text-[#2F6B57] hover:underline">Forgot password?</Link>
                </div>
                <button
                  type="button"
                  onClick={signIn}
                  disabled={busy}
                  data-testid="login-submit"
                  className="group inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-[#2F6B57] px-6 text-base font-semibold text-white transition-all duration-200 hover:bg-[#255544] disabled:opacity-60"
                >
                  {busy ? "Signing in…" : "Sign in"}
                  {!busy && <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />}
                </button>
              </div>
            ) : (
              <div className="mt-5">
                <button
                  type="button"
                  onClick={() => googleRedirect("login")}
                  disabled={busy}
                  data-testid="login-google"
                  className="inline-flex min-h-[52px] w-full items-center justify-center gap-3 rounded-full pe-surface px-6 text-base font-semibold text-[#171717] transition-colors hover:bg-[#F5F2EC] disabled:opacity-60"
                >
                  <GoogleGlyph /> Continue with Google
                </button>
                <p className="mt-3 text-center text-xs text-[#8A7C6B]">
                  You'll return here signed in. Access requires an active The Paycheck Edit purchase.
                </p>
              </div>
            )}
          </div>

          <p className="mt-5 text-center text-xs text-[#8A7C6B]">
            Don't have access yet? <Link to="/" className="font-semibold text-[#2F6B57] hover:underline">Get The Paycheck Edit</Link>
          </p>
        </div>
      </main>
      <JourneyFooter />
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}
